<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div class="min-w-0 flex-1">
                    <h1 class="stage-page-title">E-Mail-Templates</h1>
                </div>
                <div class="flex items-center">
                    <router-link class="btn-stage-icon mr-3" to="/workspace/admin/settings">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M15 18l-6-6 6-6"></path>
                        </svg>
                    </router-link>
                    <button
                        type="button"
                        class="btn btn-gray rounded shadow-sm"
                        :disabled="!selectedKey || loading"
                        @click="save"
                    >
                        Speichern
                    </button>
                </div>
            </div>
        </div>

        <div class="w-full flex-1 min-h-0 px-8 sm:px-8 lg:px-8 pt-3 pb-8 overflow-auto">
            <div class="relative">
                <loading :status="loading"/>

                <div class="grid grid-cols-1 lg:flex lg:items-start gap-6">
                    <div class="space-y-6 w-full lg:flex-shrink-0" style="max-width: 360px;">
                        <app-panel title="Templates" panel-class="overflow-visible" body-class="!p-0">
                                <button
                                    v-for="template in templateItems"
                                    :key="template.key"
                                    class="w-full text-left px-5 py-3 border-b border-bordergray-100 last:border-b-0 hover:bg-gray-50"
                                    :class="selectedKey === template.key ? 'bg-gray-100' : ''"
                                    type="button"
                                    @click="selectTemplate(template.key)"
                                >
                                    <div class="text-sm font-medium text-gray-900">{{ displayTemplateName(template) }}</div>
                                    <div class="text-xs text-gray-500 mt-1">{{ template.key }}</div>
                                </button>
                                <div v-if="templateItems.length === 0" class="px-5 py-4 text-sm text-gray-500">
                                    Keine Templates vorhanden.
                                </div>
                        </app-panel>

                        <app-panel title="System-Benachrichtigungen" panel-class="overflow-visible" body-class="!p-0">
                                <button
                                    v-for="template in systemNotificationItems"
                                    :key="template.key"
                                    class="w-full text-left px-5 py-3 border-b border-bordergray-100 last:border-b-0 hover:bg-gray-50"
                                    :class="selectedKey === template.key ? 'bg-gray-100' : ''"
                                    type="button"
                                    @click="selectTemplate(template.key)"
                                >
                                    <div class="text-xs text-gray-500">{{ displayTemplateModule(template) }}</div>
                                    <div class="text-sm font-medium text-gray-900">{{ displayTemplateName(template) }}</div>
                                    <div class="text-xs text-gray-500 mt-1">{{ template.key }}</div>
                                </button>
                                <div v-if="systemNotificationItems.length === 0" class="px-5 py-4 text-sm text-gray-500">
                                    Keine Benachrichtigungen vorhanden.
                                </div>
                        </app-panel>
                    </div>

                    <div class="space-y-6 w-full lg:min-w-0 lg:flex-1">
                        <app-panel title="Editor" panel-class="overflow-visible" body-class="!p-0">
                            <div v-if="selectedKey" class="p-6 space-y-5">
                                <div class="text-xl font-bold text-gray-900">
                                    {{ displaySelectedName }}
                                    <span class="text-base font-normal text-gray-500">({{ selectedKey }})</span>
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1" for="template-locale">Sprache</label>
                                    <select
                                        id="template-locale"
                                        v-model="locale"
                                        class="form-select block w-full"
                                        @change="reloadForLocale"
                                    >
                                        <option v-for="item in localeOptions" :key="item" :value="item">{{ item }}</option>
                                    </select>
                                </div>

                                <div v-if="!selectedIsFrameTemplate">
                                    <div class="text-sm font-medium text-gray-700 mb-2">
                                        {{ $t('Configurable for user groups') }}
                                    </div>
                                    <div class="form-selection-panel overflow-hidden">
                                        <label
                                            v-for="role in accountSettingsRoleOptions"
                                            :key="'template-role-' + role.slug"
                                            class="form-selection-option flex items-center px-3 py-2 cursor-pointer"
                                        >
                                            <input
                                                :id="'template-user-preference-role-' + role.slug"
                                                v-model="user_preference_role_slugs"
                                                class="form-checkbox h-4 w-4 text-gray-700 mr-3"
                                                :value="role.slug"
                                                type="checkbox"
                                            >
                                            <span class="text-sm text-gray-700">{{ role.name }}</span>
                                        </label>
                                        <div v-if="accountSettingsRoleOptions.length < 1" class="px-3 py-3 text-sm text-gray-500">
                                            Keine Benutzergruppen vorhanden.
                                        </div>
                                    </div>
                                    <div class="mt-2 text-xs text-gray-500">
                                        {{ $t('Only these user groups can see and manage this email notification in their account settings.') }}
                                    </div>
                                </div>

                                <div v-if="!selectedIsFrameTemplate">
                                    <label class="block text-sm font-medium text-gray-700 mb-1" for="template-subject">Betreff</label>
                                    <input
                                        id="template-subject"
                                        v-model="subject"
                                        class="form-input block w-full"
                                        type="text"
                                    >
                                </div>
                                <div v-else class="text-sm text-gray-500">
                                    Fuer Layout-Templates wird kein Betreff verwendet.
                                </div>

                                <div class="border border-bordergray-100 rounded bg-white overflow-hidden">
                                    <div class="flex flex-col xl:flex-row">
                                        <div class="p-4 flex-1 min-w-0">
                                            <label class="block text-sm font-medium text-gray-700 mb-1" for="template-html">HTML</label>
                                            <html-code-editor
                                                ref="htmlEditor"
                                                v-model="html_body"
                                                :rows="20"
                                            />
                                        </div>

                                        <div class="p-4 border-t xl:border-t-0 xl:border-l border-bordergray-100 w-full xl:w-[260px] xl:flex-shrink-0">
                                            <div class="max-h-[32rem] overflow-y-auto">
                                                <div
                                                    v-for="variable in variables"
                                                    :key="variable.key"
                                                    class="px-2 py-2 border-b border-bordergray-100 last:border-b-0 text-xs"
                                                >
                                                    <div class="font-mono text-gray-900">{{ variablePlaceholder(variable.key) }}</div>
                                                    <div class="text-gray-500 mt-1">{{ variable.description || '-' }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            <div v-else class="p-6 text-sm text-gray-500">
                                Keine Templates gefunden.
                            </div>
                        </app-panel>

                        <app-panel v-if="selectedKey" title="Vorschau" panel-class="overflow-visible" body-class="p-6 text-sm text-gray-900">
                            <template #header-actions>
                                <button
                                    type="button"
                                    class="p-0 m-0 border-0 bg-transparent leading-none text-gray-500 hover:text-gray-700 focus:outline-none"
                                    title="Vorschau aktualisieren"
                                    @click="preview"
                                >
                                    <svg-vue class="w-4 h-4" icon="font-awesome.sync-regular"></svg-vue>
                                </button>
                            </template>

                            <iframe
                                class="w-full h-[560px] border border-bordergray-100 rounded bg-white"
                                :srcdoc="previewIframeSrcdoc"
                                sandbox="allow-popups allow-popups-to-escape-sandbox"
                                title="E-Mail Vorschau"
                            ></iframe>
                            <div v-if="previewMissing.length > 0" class="mt-3 text-xs text-amber-700">
                                Fehlende Variablen: {{ previewMissing.join(', ') }}
                            </div>

                            <template #footer>
                                <form class="w-full" @submit.prevent="sendTestMail">
                                    <div class="flex items-stretch w-full">
                                        <input
                                            id="template-test-email"
                                            v-model="test_email"
                                            class="form-input block w-full rounded-r-none border-r-0"
                                            type="email"
                                            required
                                        >
                                        <button
                                            class="px-4 border border-gray-300 border-l rounded-l-none rounded-r text-sm font-medium text-white bg-gray-800 hover:bg-gray-900"
                                            type="submit"
                                        >
                                            Senden
                                        </button>
                                    </div>
                                </form>
                            </template>
                        </app-panel>

                        <app-panel title="Dokumentation" panel-class="overflow-visible" body-class="p-6">
                            <html-code-editor
                                v-model="documentation_content"
                                :rows="18"
                            />
                            <template #footer>
                                <button
                                    type="button"
                                    class="btn btn-gray rounded shadow-sm"
                                    :disabled="documentation_saving || loading"
                                    @click="saveDocumentation"
                                >
                                    Speichern
                                </button>
                            </template>
                        </app-panel>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import HtmlCodeEditor from "@/components/forms/html-code-editor";

export default {
    name: "mail-templates",
    components: {
        HtmlCodeEditor,
    },
    data() {
        return {
            loading: true,
            templates: [],
            systemLocales: [],
            selectedKey: null,
            selectedName: '',
            selectedNameTranslationKey: '',
            locale: 'en',
            localeOptions: ['en'],
            subject: '',
            html_body: '',
            variables: [],
            userRoleOptions: [],
            user_preference_role_slugs: [],
            previewSubject: '',
            previewHtml: '',
            previewMissing: [],
            test_email: '',
            documentation_content: '',
            documentation_saving: false,
        }
    },
    computed: {
        templateItems() {
            return this.templates.filter((item) => this.isTemplateKey(item.key));
        },
        systemNotificationItems() {
            return this.templates.filter((item) => !this.isTemplateKey(item.key));
        },
        selectedIsFrameTemplate() {
            return this.isTemplateKey(this.selectedKey);
        },
        accountSettingsRoleOptions() {
            return this.userRoleOptions.filter(function (role) {
                return role && role.slug && role.slug !== 'admin';
            });
        },
        displaySelectedName() {
            return this.translateLabel(this.selectedNameTranslationKey, this.selectedName || this.selectedKey || '');
        },
        previewIframeSrcdoc() {
            const rawHtml = typeof this.previewHtml === 'string' ? this.previewHtml : '';
            if (!rawHtml.trim()) {
                return '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:16px;font-family:Arial,sans-serif;color:#111;"></body></html>';
            }

            if (/<html[\s>]/i.test(rawHtml)) {
                return rawHtml;
            }

            return '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="margin:0;">' + rawHtml + '</body></html>';
        },
        loggedInEmail() {
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            if (!user || !user.email) {
                return '';
            }

            return String(user.email).trim();
        },
    },
    watch: {
        loggedInEmail: {
            immediate: true,
            handler() {
                this.ensureDefaultTestEmail();
            }
        },
    },
    mounted() {
        this.fetchUserRoleOptions();
        this.fetchTemplates();
    },
    methods: {
        fetchUserRoleOptions() {
            const self = this;
            axios.get('api/workspace/admin/settings/user-roles').then(function (response) {
                self.userRoleOptions = Array.isArray(response.data) ? response.data : [];
            }).catch(function () {
                self.userRoleOptions = [];
            });
        },
        ensureDefaultTestEmail() {
            if (this.test_email && String(this.test_email).trim() !== '') {
                return;
            }

            const email = this.loggedInEmail;
            if (!email) {
                return;
            }

            this.test_email = email;
        },
        isTemplateKey(key) {
            return typeof key === 'string' && key.indexOf('system.mail_template_') === 0;
        },
        fetchTemplates() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/settings/mail-templates').then(function (response) {
                self.templates = Array.isArray(response.data.items) ? response.data.items : [];
                self.systemLocales = self.normalizeLocaleList(response.data.supported_locales || []);

                const preferred = self.templateItems.length > 0
                    ? self.templateItems[0]
                    : (self.systemNotificationItems.length > 0 ? self.systemNotificationItems[0] : null);

                if (preferred && preferred.key) {
                    self.selectTemplate(preferred.key);
                    return;
                }

                self.loading = false;
            }).catch(function (error) {
                self.loading = false;
                self.showApiError(error, 'Templates konnten nicht geladen werden.');
                self.$router.push('/workspace/admin/settings');
            });
        },
        selectTemplate(key) {
            const self = this;
            self.selectedKey = key;
            self.loading = true;
            axios.get('api/workspace/admin/settings/mail-templates/' + encodeURIComponent(key)).then(function (response) {
                const template = response.data.template || {};
                self.systemLocales = self.normalizeLocaleList(response.data.supported_locales || self.systemLocales);
                self.selectedName = template.name || key;
                self.selectedNameTranslationKey = template.name_translation_key || '';
                self.locale = template.locale || 'en';
                self.subject = template.subject || '';
                self.html_body = template.html_body || '';
                self.variables = Array.isArray(template.variables) ? template.variables : [];
                self.user_preference_role_slugs = Array.isArray(template.user_preference_role_slugs)
                    ? template.user_preference_role_slugs.slice()
                    : [];
                self.documentation_content = self.resolveDocumentationContent(response.data.documentation_content, self.locale);
                self.localeOptions = self.collectLocaleOptions(template);
                const preferredLocale = self.pickInitialLocale(self.localeOptions, self.locale);
                if (preferredLocale !== self.locale) {
                    self.locale = preferredLocale;
                    self.reloadForLocale();
                    return;
                }
                self.preview();
            }).catch(function (error) {
                self.loading = false;
                self.showApiError(error, 'Template konnte nicht geladen werden.');
            });
        },
        reloadForLocale() {
            if (!this.selectedKey) {
                return;
            }
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/settings/mail-templates/' + encodeURIComponent(self.selectedKey), {
                params: {locale: self.locale}
            }).then(function (response) {
                const template = response.data.template || {};
                self.systemLocales = self.normalizeLocaleList(response.data.supported_locales || self.systemLocales);
                self.selectedNameTranslationKey = template.name_translation_key || self.selectedNameTranslationKey;
                self.subject = template.subject || '';
                self.html_body = template.html_body || '';
                self.variables = Array.isArray(template.variables) ? template.variables : [];
                self.user_preference_role_slugs = Array.isArray(template.user_preference_role_slugs)
                    ? template.user_preference_role_slugs.slice()
                    : [];
                self.documentation_content = self.resolveDocumentationContent(response.data.documentation_content, self.locale);
                self.localeOptions = self.collectLocaleOptions(template);
                self.preview();
            }).catch(function (error) {
                self.loading = false;
                self.showApiError(error, 'Template konnte für diese Sprache nicht geladen werden.');
            });
        },
        collectLocaleOptions(template) {
            const options = [];
            this.systemLocales.forEach(function (item) {
                if (typeof item === 'string' && item !== '') {
                    options.push(item);
                }
            });
            if (template && template.locale) {
                options.push(template.locale);
            }
            if (template && Array.isArray(template.available_locales)) {
                template.available_locales.forEach(function (item) {
                    if (typeof item === 'string' && item !== '') {
                        options.push(item);
                    }
                });
            }
            if (this.locale) {
                options.push(this.locale);
            }
            const unique = this.normalizeLocaleList(options);
            return unique.length > 0 ? unique : ['en'];
        },
        normalizeLocaleList(items) {
            const map = {};
            (Array.isArray(items) ? items : []).forEach(function (item) {
                if (typeof item !== 'string') {
                    return;
                }
                const trimmed = item.trim().toLowerCase();
                if (!trimmed) {
                    return;
                }
                const normalized = trimmed.split('-')[0].split('_')[0];
                if (normalized) {
                    map[normalized] = normalized;
                }
            });
            return Object.keys(map).sort(function (a, b) {
                return a.localeCompare(b);
            });
        },
        pickInitialLocale(options, currentLocale) {
            const list = Array.isArray(options) ? options : [];
            if (list.includes('de')) {
                return 'de';
            }
            if (currentLocale && list.includes(currentLocale)) {
                return currentLocale;
            }
            if (list.length > 0) {
                return list[0];
            }
            return currentLocale || 'en';
        },
        variablePlaceholder(variableKey) {
            return '{{' + variableKey + '}}';
        },
        translateLabel(key, fallback) {
            const normalizedFallback = typeof fallback === 'string' ? fallback : '';
            if (!key || typeof key !== 'string') {
                return normalizedFallback;
            }
            const translated = this.$t(key);
            if (typeof translated === 'string' && translated !== '' && translated !== key) {
                return translated;
            }
            return normalizedFallback;
        },
        displayTemplateName(template) {
            const fallback = template && template.name ? template.name : (template && template.key ? template.key : '');
            const translationKey = template && template.name_translation_key ? template.name_translation_key : '';
            return this.translateLabel(translationKey, fallback);
        },
        displayTemplateModule(template) {
            const fallback = template && template.module ? template.module : 'System';
            const translationKey = template && template.module_translation_key ? template.module_translation_key : '';
            return this.translateLabel(translationKey, fallback);
        },
        resolveDocumentationContent(content, locale) {
            const value = typeof content === 'string' ? content : '';
            if (value.trim() !== '') {
                return value;
            }
            return this.defaultDocumentationContent(locale);
        },
        defaultDocumentationContent(locale) {
            const isGerman = String(locale || '').toLowerCase() === 'de';
            if (isGerman) {
                return [
                    '<!-- IF-Syntax für Mail-Templates -->',
                    '',
                    '<!-- 1) Wahr/Falsch (truthy check) -->',
                    '<%IF var%>',
                    '    <p>Dieser Block wird angezeigt, wenn "var" wahr ist.</p>',
                    '<%ENDIF%>',
                    '',
                    '<!-- 2) Gleichheit -->',
                    '<%IF var == "value"%>',
                    '    <p>Dieser Block wird angezeigt, wenn var exakt "value" ist.</p>',
                    '<%ENDIF%>',
                    '',
                    '<!-- 3) Ungleichheit -->',
                    '<%IF var != "value"%>',
                    '    <p>Dieser Block wird angezeigt, wenn var nicht "value" ist.</p>',
                    '<%ENDIF%>',
                    '',
                    '<!-- Variablen im Inhalt -->',
                    '<p>Hallo {{recipient_name}},</p>',
                    '<p>Status: {{status_to}}</p>',
                    '<p>Status-ID (neu): {{status_to_id}}</p>',
                    '<p>Status-Farbe (neu): {{status_to_color}}</p>',
                    '',
                    '<!-- Beispiel für Finance-Status -->',
                    '<%IF status_to_id == "3"%>',
                    '    <p>Bezahlt am: {{paid_at}}</p>',
                    '<%ENDIF%>',
                    '<p><span style="display:inline-block;background:{{status_to_color}};color:#fff;padding:4px 10px;border-radius:4px;">{{status_to}}</span></p>',
                    '',
                    '<!-- Hinweise -->',
                    '<!-- - Es wird kein PHP oder Script-Code ausgefuehrt. -->',
                    '<!-- - Platzhalter bleiben {{variable_name}}. -->',
                    '<!-- - In IF-Ausdruecken nur Variablennamen verwenden: <%IF var%>. -->',
                ].join('\n');
            }

            return [
                '<!-- IF syntax for mail templates -->',
                '',
                '<!-- 1) True/false (truthy check) -->',
                '<%IF var%>',
                '    <p>This block is rendered when "var" is truthy.</p>',
                '<%ENDIF%>',
                '',
                '<!-- 2) Equality -->',
                '<%IF var == "value"%>',
                '    <p>This block is rendered when var exactly equals "value".</p>',
                '<%ENDIF%>',
                '',
                '<!-- 3) Inequality -->',
                '<%IF var != "value"%>',
                '    <p>This block is rendered when var is not "value".</p>',
                '<%ENDIF%>',
                '',
                '<!-- Variables in content -->',
                '<p>Hello {{recipient_name}},</p>',
                '<p>Status: {{status_to}}</p>',
                '<p>Status ID (new): {{status_to_id}}</p>',
                '<p>Status color (new): {{status_to_color}}</p>',
                '',
                '<!-- Example for finance status -->',
                '<%IF status_to_id == "3"%>',
                '    <p>Paid at: {{paid_at}}</p>',
                '<%ENDIF%>',
                '<p><span style="display:inline-block;background:{{status_to_color}};color:#fff;padding:4px 10px;border-radius:4px;">{{status_to}}</span></p>',
                '',
                '<!-- Notes -->',
                '<!-- - No PHP or script code is executed. -->',
                '<!-- - Placeholders remain {{variable_name}}. -->',
                '<!-- - In IF expressions use variable names only: <%IF var%>. -->',
            ].join('\n');
        },
        saveDocumentation() {
            const self = this;
            self.documentation_saving = true;
            axios.post('api/workspace/admin/settings/mail-templates/documentation', {
                locale: self.locale,
                content: self.documentation_content || ''
            }).then(function () {
                self.documentation_saving = false;
                self.$notify({
                    title: 'Erfolg',
                    text: 'Dokumentation wurde gespeichert.',
                    type: 'success'
                });
            }).catch(function (error) {
                self.documentation_saving = false;
                self.showApiError(error, 'Dokumentation konnte nicht gespeichert werden.');
            });
        },
        preview() {
            const self = this;
            if (!self.selectedKey) {
                self.loading = false;
                return;
            }

            axios.post('api/workspace/admin/settings/mail-templates/' + encodeURIComponent(self.selectedKey) + '/preview', {
                locale: self.locale
            }).then(function (response) {
                const preview = response.data.preview || {};
                self.previewSubject = preview.subject || '';
                self.previewHtml = preview.html_body || '';
                self.previewMissing = Array.isArray(preview.missing_variables) ? preview.missing_variables : [];
                self.loading = false;
            }).catch(function (error) {
                self.previewSubject = '';
                self.previewHtml = '';
                self.previewMissing = [];
                self.loading = false;
                self.showApiError(error, 'Vorschau konnte nicht geladen werden.');
            });
        },
        save() {
            const self = this;
            if (!self.selectedKey) {
                return;
            }
            const normalizedSubject = String(self.subject || '').trim() !== ''
                ? self.subject
                : (self.selectedName || self.selectedKey || 'Template');
            self.loading = true;
            axios.post('api/workspace/admin/settings/mail-templates/' + encodeURIComponent(self.selectedKey), {
                locale: self.locale,
                subject: normalizedSubject,
                html_body: self.html_body,
                user_preference_role_slugs: self.selectedIsFrameTemplate ? [] : self.user_preference_role_slugs
            }).then(function () {
                self.$notify({
                    title: 'Erfolg',
                    text: 'Template wurde gespeichert.',
                    type: 'success'
                });
                self.preview();
            }).catch(function (error) {
                self.loading = false;
                self.showApiError(error, 'Template konnte nicht gespeichert werden.');
            });
        },
        sendTestMail() {
            const self = this;
            if (!self.selectedKey || !self.test_email) {
                return;
            }
            self.loading = true;
            axios.post('api/workspace/admin/settings/mail-templates/' + encodeURIComponent(self.selectedKey) + '/test-mail', {
                locale: self.locale,
                to_email: self.test_email
            }).then(function () {
                self.loading = false;
                self.$notify({
                    title: 'Erfolg',
                    text: 'Testmail wurde versendet.',
                    type: 'success'
                });
            }).catch(function (error) {
                self.loading = false;
                self.showApiError(error, 'Testmail konnte nicht versendet werden.');
            });
        },
        showApiError(error, fallback) {
            const message = this.getApiErrorMessage(error, fallback);
            this.$notify({
                title: 'Fehler',
                text: message,
                type: 'error'
            });
        },
        getApiErrorMessage(error, fallback) {
            if (error && error.response && error.response.data) {
                const responseData = error.response.data;
                if (responseData.message) {
                    return responseData.message;
                }
                if (responseData.errors && typeof responseData.errors === 'object') {
                    const firstField = Object.keys(responseData.errors)[0];
                    if (firstField && responseData.errors[firstField] && responseData.errors[firstField][0]) {
                        return responseData.errors[firstField][0];
                    }
                }
            }

            return fallback;
        },
    }
}
</script>
